import { useMemo, useState } from "react";
import { DollarSignIcon } from "lucide-react";

import { EarningsPeriodDropdown } from "./EarningsPeriodDropdown";
import { EarningsSectionLabel } from "./EarningsSectionLabel";
import { EarningsTrackerRow } from "./EarningsTrackerRow";
import { EarningsYearDropdown } from "./EarningsYearDropdown";
import { EarningsTrackerTotalsFooter } from "./EarningsTrackerTotalsFooter";

import { Column } from "@/components/Column";
import { EmptyState } from "@/components/EmptyState";
import { ErrorText } from "@/components/ErrorText";
import { Row } from "@/components/Row";
import { SortableHeader } from "@/components/SortableHeader";
import { Text } from "@/components/Text";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useGetStafferEarnings } from "@/hooks/queries/useGetStafferEarnings";
import { ADMIN_USER_IDS } from "@/lib/constants";
import { earningsTracker } from "@/lib/earningsTracker";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cycleColumnSort } from "@/lib/sort";
import { cn } from "@/lib/utils";
import {
  EarningsPeriod,
  EarningsTrackerFilter,
  EarningsTrackerSortKey,
  EarningsTrackerSortState,
  SortOrder,
} from "@/types";

const COLUMN_WIDTHS = [
  "16%",
  "9%",
  "9%",
  "10%",
  "12%",
  "10%",
  "11%",
  "8%",
  "11%",
  "4%",
];

const PROBATIONARY_TOOLTIP =
  "Placements that have yet to hit their backfill guarantee";

const filterToLabel: Record<EarningsTrackerFilter, string> = {
  [EarningsTrackerFilter.ALL]: "All",
  [EarningsTrackerFilter.PAID]: "Paid",
  [EarningsTrackerFilter.UNPAID]: "Unpaid",
  [EarningsTrackerFilter.PROBATIONARY]: "Probationary",
  [EarningsTrackerFilter.BACKFILL]: "Backfill",
  [EarningsTrackerFilter.CLAIMED]: "Claimed",
};

const defaultSort: EarningsTrackerSortState = {
  key: EarningsTrackerSortKey.CREDITED_DATE,
  direction: SortOrder.Desc,
};

export const EarningsTrackerSection = ({
  selectedUserId,
}: {
  selectedUserId?: number;
}) => {
  /* Database CRUD Operations */
  // Read
  const {
    data: earnings,
    isPending,
    isError,
  } = useGetStafferEarnings({ userId: selectedUserId });
  const currentUser = useGetCurrentUser();

  /* React Hooks */
  const [filter, setFilter] = useState(EarningsTrackerFilter.ALL);
  const [period, setPeriod] = useState(EarningsPeriod.ALL_YEAR);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [sort, setSort] = useState<EarningsTrackerSortState | null>(
    defaultSort,
  );

  // No explicit selection means the endpoint served the current user's page
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const viewedUserId = selectedUserId || currentUser?.id;
  const isAdjustmentAdmin =
    !!currentUser && ADMIN_USER_IDS.includes(currentUser.id);

  const sortedRows = useMemo(() => {
    const filteredRows = (earnings?.placements ?? []).filter(
      (placement) =>
        earningsTracker.matchesPeriod(placement, period, selectedYear) &&
        earningsTracker.matchesFilter(placement, filter, viewedUserId),
    );

    return earningsTracker.sortPlacements(filteredRows, sort);
  }, [earnings, filter, period, selectedYear, sort, viewedUserId]);

  /* Helpers */
  // Claimed placements only count toward the total when they're what's being viewed
  const totals = useMemo(() => {
    const totalRows =
      filter === EarningsTrackerFilter.CLAIMED
        ? sortedRows
        : sortedRows.filter((placement) => !placement.isSuperseded);

    return totalRows.reduce(
      (acc, placement) => ({
        commission:
          acc.commission +
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          (earningsTracker.getCommissionDollars(placement) || 0),
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        revenue: acc.revenue + (placement.revenueEarned || 0),
      }),
      { commission: 0, revenue: 0 },
    );
  }, [filter, sortedRows]);

  /* Handlers */
  const handleSort = (key: EarningsTrackerSortKey) => {
    const next = cycleColumnSort(
      sort,
      key,
      (k) => k === EarningsTrackerSortKey.CANDIDATE,
    );
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.EARNINGS,
      "Sort by",
      next ? `${next.key} ${next.direction}` : "none",
    );
    setSort(next);
  };

  const handleFilterChange = (nextFilter: EarningsTrackerFilter) => {
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.EARNINGS,
      "Commission tracker filter",
      nextFilter,
    );
    setFilter(nextFilter);
  };

  const handlePeriodChange = (nextPeriod: EarningsPeriod) => {
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.EARNINGS,
      "Commission tracker period",
      nextPeriod,
    );
    setPeriod(nextPeriod);
  };

  const handleYearChange = (nextYear: number) => {
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.EARNINGS,
      "Commission tracker year",
      String(nextYear),
    );
    setSelectedYear(nextYear);
    // Sub-year periods only make sense relative to today
    setPeriod(EarningsPeriod.ALL_YEAR);
  };

  /* Render */
  const renderFilterPill = (pillFilter: EarningsTrackerFilter) => {
    const pill = (
      <Button
        key={pillFilter}
        type="button"
        variant="ghost"
        size="sm"
        className={cn(
          "h-7 rounded-full border border-slate-200 px-3 text-xs font-medium hover:bg-slate-50",
          filter === pillFilter
            ? "border-primary-300 bg-primary-100 text-primary-800 hover:bg-primary-100"
            : "bg-white text-slate-600",
        )}
        onClick={() => {
          handleFilterChange(pillFilter);
        }}
      >
        {filterToLabel[pillFilter]}
      </Button>
    );

    if (pillFilter !== EarningsTrackerFilter.PROBATIONARY) {
      return pill;
    }

    return (
      <Tooltip key={pillFilter}>
        <TooltipTrigger asChild>{pill}</TooltipTrigger>
        <TooltipContent side="top">{PROBATIONARY_TOOLTIP}</TooltipContent>
      </Tooltip>
    );
  };

  const renderBody = () => {
    if (sortedRows.length === 0) {
      return (
        <TableRow>
          <TableCell className="p-0" colSpan={COLUMN_WIDTHS.length}>
            <EmptyState
              className="rounded-b-lg bg-slate-50 py-12"
              description="Placements with earnings will appear here."
              headerClassName="text-xl"
              Icon={DollarSignIcon}
              title="No placements yet"
            />
          </TableCell>
        </TableRow>
      );
    }

    return sortedRows.map((placement) => (
      <EarningsTrackerRow
        key={placement.id}
        isAdjustmentAdmin={isAdjustmentAdmin}
        placement={placement}
        viewedUserId={viewedUserId}
        viewedYear={selectedYear}
      />
    ));
  };

  const renderSkeleton = () => (
    <Column className="gap-3">
      <Skeleton className="h-3 w-44" />
      <Skeleton className="h-64 w-full rounded-lg" />
    </Column>
  );

  const render = () => {
    if (isPending) {
      return renderSkeleton();
    }

    if (isError) {
      return <ErrorText errorMessage="Failed to load commission tracker." />;
    }

    return (
      <TooltipProvider delayDuration={200}>
        <Column className="min-w-0 gap-3">
          {/* Label row and table header pin to the top edge while the rows scroll */}
          <Row className="sticky top-0 z-30 h-10 min-h-10 items-center justify-between gap-3 bg-slate-50">
            <Row className="min-w-0 items-center gap-3">
              <EarningsSectionLabel className="shrink-0">
                Commission tracker
              </EarningsSectionLabel>
              <Row className="items-center gap-1">
                {Object.values(EarningsTrackerFilter).map(renderFilterPill)}
              </Row>
            </Row>
            <Row className="shrink-0 items-center gap-2">
              {selectedYear === new Date().getFullYear() ? (
                <EarningsPeriodDropdown
                  period={period}
                  onPeriodChange={handlePeriodChange}
                />
              ) : null}
              <EarningsYearDropdown
                year={selectedYear}
                onYearChange={handleYearChange}
              />
            </Row>
          </Row>
          <Column className="gap-0">
            <Card className="border-slate-200 shadow-none">
              <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
                <colgroup>
                  {COLUMN_WIDTHS.map((width, index) => (
                    <col key={index} style={{ width }} />
                  ))}
                </colgroup>
                <TableHeader className="sticky top-10 z-20 bg-slate-50 [&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-slate-200 [&_tr_th:first-child]:rounded-tl-lg [&_tr_th:last-child]:rounded-tr-lg">
                  <TableRow>
                    <SortableHeader
                      sortKey={EarningsTrackerSortKey.CANDIDATE}
                      label="Candidate"
                      sort={sort}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      sortKey={EarningsTrackerSortKey.CREDITED_DATE}
                      label="Credited"
                      sort={sort}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      sortKey={EarningsTrackerSortKey.START_DATE}
                      label="Start date"
                      sort={sort}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      sortKey={EarningsTrackerSortKey.RETENTION}
                      label="Retention"
                      sort={sort}
                      onSort={handleSort}
                    />
                    <TableHead>
                      <Text className="text-sm font-medium text-slate-500">
                        Employment
                      </Text>
                    </TableHead>
                    <SortableHeader
                      sortKey={EarningsTrackerSortKey.REVENUE}
                      label="Revenue"
                      sort={sort}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      sortKey={EarningsTrackerSortKey.COMMISSION}
                      label="Commission"
                      sort={sort}
                      onSort={handleSort}
                    />
                    <TableHead>
                      <Text className="text-sm font-medium text-slate-500">
                        Rate
                      </Text>
                    </TableHead>
                    <TableHead>
                      <Text className="text-sm font-medium text-slate-500">
                        Status
                      </Text>
                    </TableHead>
                    {/* Admin-only adjustment button column */}
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody className="[&_td]:py-4 [&_td]:align-middle [&_tr]:border-slate-200">
                  {renderBody()}
                </TableBody>
              </table>
            </Card>
            {sortedRows.length > 0 ? (
              <EarningsTrackerTotalsFooter
                columnWidths={COLUMN_WIDTHS}
                isDisabled={filter === EarningsTrackerFilter.CLAIMED}
                totalCommission={totals.commission}
                totalRevenue={totals.revenue}
              />
            ) : null}
          </Column>
        </Column>
      </TooltipProvider>
    );
  };

  return render();
};
